import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  LayoutDashboard,
  GraduationCap,
  Users,
  CalendarCheck,
  FileBarChart,
  Wallet,
  Briefcase,
  Settings,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  Search,
  Bell,
  CalendarRange,
  BookOpen,
  Layers,
  FileText,
  UserCheck,
  DoorOpen,
  Fingerprint,
  CalendarDays,
  BookMarked,
  PackageSearch,
  Video,
  ShieldCheck,
  ArrowLeftRight,
  Megaphone,
  Laptop,
  HelpCircle,
  NotebookText,
  HeartHandshake,
  ClipboardList,
  Images,
  FileSignature,
  Award,
  Trophy,
  Activity,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { AcademicYearsApi } from '@/services/academic.api';
import { useTerminology } from '@/hooks/useTerminology';
import { usePermissions } from '@/hooks/usePermissions';
import { canAccessPath } from '@/lib/access';
import { TrialBanner } from './TrialBanner';
import { SchoolSwitcher } from './SchoolSwitcher';
import { NotificationsBell } from './NotificationsBell';
import { cn } from '@/lib/cn';
import { AuthApi } from '@/services/auth.api';

/** Same public, pre-auth-safe endpoint the org/superadmin layouts already use (see PublicService.branding()) — a tenant-scope token has no permission to the authenticated `/platform/settings/general` endpoint. */
async function fetchBranding() {
  const { data } = await axios.get('/api/v1/public/branding');
  return (data?.data ?? data) as { name: string; themeColor: string; horizontalLogoUrl: string | null; verticalLogoUrl: string | null };
}

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

interface NavGroup {
  title: string;
  /** Icon shown on the always-visible left rail — every group is reachable
   * directly from the rail, same "no module-picker gating" pattern as the
   * org-admin layout. */
  icon: LucideIcon;
  sections: NavSection[];
}

const groups: NavGroup[] = [
  {
    title: 'Overview',
    icon: LayoutDashboard,
    sections: [
      {
        items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true }],
      },
    ],
  },
  {
    title: 'Academic Setup',
    icon: GraduationCap,
    sections: [
      {
        items: [
          { to: '/setup/classes', label: 'Classes & Sections', icon: Layers },
          { to: '/setup/class-subjects', label: 'Class Subject', icon: BookMarked },
          { to: '/setup/section-teachers', label: 'Class Section & Teachers', icon: Users },
          { to: '/setup/subjects', label: 'Subjects', icon: BookOpen },
          { to: '/setup/lessons', label: 'Lessons & Topics', icon: NotebookText },
          { to: '/setup/custom-fields', label: 'Custom Fields', icon: ClipboardList },
          { to: '/promotion', label: 'Promotion', icon: GraduationCap },
          { to: '/academic-calendar', label: 'Academic Calendar', icon: CalendarDays },
        ],
      },
    ],
  },
  {
    title: 'Noorani',
    icon: Video,
    sections: [
      {
        items: [
          { to: '/noorani-attendance', label: 'Noorani Attendance Report', icon: CalendarCheck, end: true },
          { to: '/noorani-attendance/teachers', label: 'Noorani Teacher Attendance', icon: CalendarCheck },
          { to: '/noorani-tutorials', label: 'Noorani Tutorials', icon: Video },
        ],
      },
    ],
  },
  {
    title: 'People',
    icon: Users,
    sections: [
      {
        items: [
          { to: '/students', label: 'Students', icon: GraduationCap },
          { to: '/online-admissions', label: 'Online Admission', icon: UserCheck },
          { to: '/staff', label: 'Staff', icon: Briefcase },
          { to: '/parents', label: 'Parents', icon: Users },
          { to: '/affidavits', label: 'Affidavits', icon: FileSignature },
          { to: '/students/transfer', label: 'Transfer Students', icon: ArrowLeftRight },
          { to: '/students/class-change-requests', label: 'Class Change Requests', icon: ArrowLeftRight },
          { to: '/staff/class-change-requests', label: 'Teacher Class Change Requests', icon: ArrowLeftRight },
        ],
      },
    ],
  },
  {
    title: 'Front Office',
    icon: DoorOpen,
    sections: [
      {
        items: [
          { to: '/visitors', label: 'Visitors', icon: UserCheck },
          { to: '/visits', label: 'Visits', icon: DoorOpen },
          { to: '/pgc-registrations', label: 'PGC Registrations', icon: HeartHandshake },
        ],
      },
    ],
  },
  {
    title: 'Attendance',
    icon: CalendarCheck,
    sections: [
      {
        items: [
          { to: '/attendance/view', label: 'View Attendance', icon: CalendarCheck },
          { to: '/attendance/summary', label: 'Attendance Summary', icon: CalendarCheck },
          { to: '/attendance/staff', label: 'View Attendance (Staff)', icon: CalendarCheck },
          { to: '/attendance/teachers/summary', label: 'Teacher Attendance Summary', icon: CalendarCheck },
          { to: '/attendance/admins', label: 'Admin Attendance', icon: CalendarCheck },
          { to: '/guardian-attendance', label: 'Parent Attendance', icon: CalendarCheck, end: true },
          { to: '/guardian-attendance/summary', label: 'Parent Attendance Summary', icon: CalendarCheck },
        ],
      },
    ],
  },
  {
    title: 'Fees',
    icon: Wallet,
    sections: [
      {
        items: [
          { to: '/fees', label: 'Fee Report', icon: Wallet, end: true },
          { to: '/fees/adjustment', label: 'Fees Adjustment', icon: Wallet },
          { to: '/fees/demands', label: 'Fees Demands', icon: Wallet },
          { to: '/ho-fees-share', label: 'Head Office Fees Share', icon: Wallet },
          { to: '/expense-categories', label: 'Expense Categories', icon: Wallet },
          { to: '/expenses', label: 'Manage Expenses', icon: Wallet },
        ],
      },
      {
        label: 'Noorani',
        items: [
          { to: '/fees/noorani', label: 'Noorani Fees', icon: Wallet, end: true },
          { to: '/fees/noorani/report', label: 'Noorani Fee Report', icon: Wallet },
        ],
      },
    ],
  },
  {
    title: 'Quran Tracker',
    icon: BookOpen,
    sections: [
      {
        items: [
          { to: '/quran-tracker/targets', label: 'QT Target', icon: BookOpen },
          { to: '/quran-tracker/student-targets', label: 'QT Students Target', icon: Users },
        ],
      },
    ],
  },
  {
    title: 'Operations',
    icon: ClipboardList,
    sections: [
      {
        label: 'Exams & Assessment',
        items: [
          { to: '/exams', label: 'Exams', icon: FileBarChart },
          { to: '/online-exams', label: 'Online Exams', icon: Laptop },
          { to: '/online-exams/question-bank', label: 'Question Bank', icon: HelpCircle },
          { to: '/assignments', label: 'Assignments', icon: ClipboardList },
          { to: '/co-scholastic', label: 'Co-scholastic Assessment', icon: Award },
        ],
      },
      {
        label: 'Timetable',
        items: [
          { to: '/timetable', label: 'Timetable', icon: CalendarRange },
          { to: '/timetable/teacher', label: 'Teacher Timetable', icon: CalendarRange },
        ],
      },
      {
        label: 'Communication',
        items: [
          { to: '/notifications', label: 'Notifications', icon: Bell },
          { to: '/announcements', label: 'Announcements', icon: Megaphone },
          { to: '/sliders', label: 'Sliders', icon: Images },
        ],
      },
      {
        label: 'Program',
        items: [{ to: '/program-candidates', label: 'Program Candidate Registration', icon: Trophy }],
      },
      {
        label: 'Security',
        items: [
          { to: '/biometric-devices', label: 'Biometric Devices', icon: Fingerprint },
          { to: '/id-cards/verify', label: 'Verify ID Cards', icon: ShieldCheck },
        ],
      },
    ],
  },
  {
    title: 'Material Management',
    icon: PackageSearch,
    sections: [
      {
        items: [{ to: '/material-requests', label: 'Material Requests', icon: PackageSearch }],
      },
    ],
  },
  {
    title: 'Reports',
    icon: FileBarChart,
    sections: [
      {
        label: 'Student',
        items: [
          { to: '/report-cards', label: 'Report Card', icon: FileText },
          { to: '/reports/students/summary', label: 'Student Summary Report', icon: GraduationCap },
          { to: '/reports/students', label: 'Students Report', icon: GraduationCap },
          { to: '/reports/students/portfolio', label: 'Student Portfolio', icon: GraduationCap },
        ],
      },
      {
        // Student Attendance Summary is listed here too (not just under
        // the standalone Attendance group) — same page, same reasoning as
        // Student Portfolio/Online Admission appearing in more than one
        // nav group elsewhere in this app: a user browsing Reports for
        // "the attendance reports" shouldn't have to know a summary report
        // happens to live in a different top-level group to find it.
        label: 'Attendance',
        items: [
          { to: '/reports/attendance/students', label: 'Student Attendance Report', icon: CalendarCheck },
          { to: '/reports/attendance/students/summary', label: 'Student Attendance Summary', icon: CalendarCheck },
          { to: '/reports/attendance/students/detailed', label: 'Detailed Student Attendance', icon: CalendarCheck },
          { to: '/reports/attendance/teachers/detailed', label: 'Detailed Teacher Attendance', icon: CalendarCheck },
        ],
      },
      {
        label: 'Teacher',
        items: [{ to: '/reports/teachers', label: 'Teachers Report', icon: Briefcase }],
      },
      {
        label: 'Staff',
        items: [
          { to: '/leave-management', label: 'Staff Leave', icon: CalendarCheck, end: true },
          { to: '/leave-management/report', label: 'Leave Report', icon: FileBarChart },
        ],
      },
      {
        label: 'Finance',
        items: [
          { to: '/reports/finance', label: 'Finance Report', icon: Wallet },
          { to: '/mis-reports', label: 'MIS Reports', icon: Wallet },
        ],
      },
      {
        label: 'General',
        items: [
          { to: '/reports/login', label: 'Login Report', icon: FileBarChart },
          { to: '/reports/export', label: 'Data Export', icon: FileBarChart },
          { to: '/reports/video-view', label: 'Video View Report', icon: Video },
        ],
      },
    ],
  },
  {
    title: 'Transfer Certificates',
    icon: FileText,
    sections: [
      {
        items: [{ to: '/transfer-certificates', label: 'Transfer Certificates', icon: FileText }],
      },
    ],
  },
  {
    // Pinned last on the rail — same "always reachable, but not competing
    // for top-of-list attention" placement as the org/superadmin Settings
    // groups.
    title: 'System',
    icon: Settings,
    sections: [
      {
        items: [
          { to: '/billing', label: 'Billing', icon: Wallet },
          { to: '/settings', label: 'Settings', icon: Settings },
          { to: '/usage-history', label: 'Usage History', icon: Activity },
        ],
      },
    ],
  },
];

// A `NavLink` without `end` matches on startsWith — so an item like
// 'Students Report' ('/reports/students') stays lit up on top of its own
// more specific sibling 'Student Portfolio' ('/reports/students/portfolio'),
// since that path literally starts with the first one. Pre-index, once at
// module scope, every item path that's a strict prefix of another item's
// path — then at render time, defer to the more specific sibling when both
// match, same "most specific wins" rule, just for path prefixes instead of
// query strings. Deliberately NOT just adding `end: true` everywhere: some
// paths (e.g. '/students') have real sub-pages with no nav item of their own
// ('/students/:id', '/students/new') — `end: true` there would leave NO
// nav item highlighted on those pages, which is worse than the original bug.
const allNavItems = groups.flatMap((g) => g.sections).flatMap((s) => s.items);
const longerSiblingPathsByPath = new Map<string, string[]>();
for (const item of allNavItems) {
  for (const other of allNavItems) {
    if (other.to !== item.to && other.to.startsWith(item.to + '/')) {
      const arr = longerSiblingPathsByPath.get(item.to) ?? [];
      arr.push(other.to);
      longerSiblingPathsByPath.set(item.to, arr);
    }
  }
}

function isNavItemActive(to: string, pathname: string, end?: boolean): boolean {
  const matches = end ? pathname === to : pathname === to || pathname.startsWith(to + '/');
  if (!matches) return false;
  if (end) return true;
  const longerSiblings = longerSiblingPathsByPath.get(to);
  if (longerSiblings?.some((p) => pathname === p || pathname.startsWith(p + '/'))) return false;
  return true;
}

/** The group's own landing page — where a rail-icon click navigates to. */
function firstItemPath(group: NavGroup): string {
  for (const section of group.sections) {
    if (section.items.length) return section.items[0].to;
  }
  return '/dashboard';
}

/** Which group "owns" the current URL — longest matching item path wins —
 * used to auto-select the right rail icon/panel on load, refresh, or
 * browser back/forward. Operates on the already permission-filtered group
 * list so it never selects a group the current user can't see. */
function groupForPath(pathname: string, groupList: NavGroup[]): NavGroup | undefined {
  let best: { group: NavGroup; len: number } | undefined;
  for (const group of groupList) {
    for (const section of group.sections) {
      for (const item of section.items) {
        if (pathname === item.to || pathname.startsWith(item.to + '/')) {
          if (!best || item.to.length > best.len) best = { group, len: item.to.length };
        }
      }
    }
  }
  return best?.group;
}

export function SchoolLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, schoolSlug, refreshToken } = useAuthStore();
  const [panelOpen, setPanelOpen] = useState(true);
  const term = useTerminology();
  const { ctx } = usePermissions();
  const queryClient = useQueryClient();
  const { data: currentAcademicYear } = useQuery({
    queryKey: ['academic-years', 'current', schoolSlug],
    queryFn: AcademicYearsApi.current,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const { data: branding } = useQuery({ queryKey: ['public-branding'], queryFn: fetchBranding, staleTime: 5 * 60 * 1000 });
  const logoUrl = branding?.verticalLogoUrl ?? branding?.horizontalLogoUrl ?? null;

  // Groups/sections filtered by the user's role — mirrors the previous
  // flat filtering, just nested one level deeper.
  const visibleGroups = useMemo(
    () =>
      groups
        .map((g) => ({
          ...g,
          sections: g.sections
            .map((s) => ({ ...s, items: s.items.filter((it) => canAccessPath(ctx, it.to)) }))
            .filter((s) => s.items.length > 0),
        }))
        .filter((g) => g.sections.length > 0),
    [ctx],
  );

  const [activeGroupTitle, setActiveGroupTitle] = useState(
    () => groupForPath(location.pathname, visibleGroups)?.title ?? visibleGroups[0]?.title,
  );

  // Keep the active rail icon/panel in sync with direct navigation (deep
  // links, browser back/forward, a sub-item link inside the panel itself)
  // — not just clicks on the rail.
  useEffect(() => {
    const group = groupForPath(location.pathname, visibleGroups);
    if (group) setActiveGroupTitle(group.title);
  }, [location.pathname, visibleGroups]);

  const activeGroup = visibleGroups.find((g) => g.title === activeGroupTitle) ?? visibleGroups[0];
  // A group with exactly one item (e.g. "Overview") has nothing left to
  // disambiguate once its rail icon is clicked — showing a flyout panel
  // with that same single link is pure redundancy, not real navigation.
  const activeGroupItemCount = activeGroup?.sections.reduce((n, s) => n + s.items.length, 0) ?? 0;
  const showPanel = panelOpen && activeGroupItemCount > 1;

  // School-configurable labels for level/group-related nav items.
  const labelFor = (to: string, fallback: string) => {
    if (to === '/setup/classes')
      return `${term.levelPlural} & ${term.groupPlural}`;
    return fallback;
  };

  const handleLogout = async () => {
    // Best-effort — logoutTenant() no-ops for non-tenant-scope refresh
    // tokens, so this is safe to call regardless of session type. Never
    // block logout on the network call failing/timing out.
    if (refreshToken) {
      await AuthApi.logout(refreshToken).catch(() => undefined);
    }
    queryClient.clear();
    logout();
    navigate('/login');
  };

  function selectGroup(group: NavGroup) {
    setActiveGroupTitle(group.title);
    setPanelOpen(true);
    navigate(firstItemPath(group));
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Icon rail — every category is always reachable here, same pattern
          as the org-admin layout. Light theme (white, not the old
          bg-slate-900 black) with a labeled icon per group, matching a
          light CRM-style rail — wider than the old icon-only 64px rail to
          fit the label underneath. */}
      <aside className="flex w-20 shrink-0 flex-col items-center border-r border-slate-200 bg-white py-4">
        {/* Brand badge — the org's own logo once set in General Settings
            (platform tier → /platform/settings/general), falling back to
            the generic GraduationCap icon until one is uploaded.
            Deliberately NOT the active-nav-icon color, which would read
            as "always selected". */}
        <div
          className="mb-4 flex h-9 w-9 items-center justify-center overflow-hidden rounded-md bg-slate-100 ring-1 ring-slate-200"
          title={schoolSlug ? schoolSlug.toUpperCase() : 'School'}
        >
          {logoUrl ? (
            <img src={logoUrl} alt={branding?.name ?? 'Logo'} className="h-full w-full object-contain" />
          ) : (
            <GraduationCap className="h-5 w-5 text-brand-600" />
          )}
        </div>

        <nav className="flex flex-1 flex-col items-center gap-1 overflow-y-auto">
          {visibleGroups.map((group) => {
            const Icon = group.icon;
            const isActive = group.title === activeGroupTitle;
            return (
              <button
                key={group.title}
                type="button"
                onClick={() => selectGroup(group)}
                title={group.title}
                className={cn(
                  'flex w-16 shrink-0 flex-col items-center gap-0.5 rounded-md px-1 py-1.5 transition',
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900',
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="w-full truncate text-center text-[9px] font-medium leading-tight">{group.title}</span>
              </button>
            );
          })}
        </nav>

        {activeGroupItemCount > 1 && (
          <button
            type="button"
            onClick={() => setPanelOpen((v) => !v)}
            className="mt-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            title={panelOpen ? 'Hide panel' : 'Show panel'}
          >
            {panelOpen ? <ChevronsLeft className="h-4 w-4" /> : <ChevronsRight className="h-4 w-4" />}
          </button>
        )}
      </aside>

      {/* Flyout panel — the active group's own items. */}
      {showPanel && activeGroup && (
        <aside className="flex w-64 shrink-0 flex-col overflow-y-auto border-r border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-4">
            <div className="text-sm font-semibold text-slate-900">{activeGroup.title}</div>
          </div>
          <nav className="flex-1 px-3 py-3">
            {activeGroup.sections.map((section, sIdx) => (
              <div key={section.label ?? sIdx} className={sIdx > 0 ? 'mt-3' : undefined}>
                {section.label && (
                  <div className="mb-1 px-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    {section.label}
                  </div>
                )}
                <ul className="space-y-0.5">
                  {section.items.map(({ to, label, icon: Icon, end }) => {
                    const display = labelFor(to, label);
                    return (
                      <li key={to}>
                        <NavLink
                          to={to}
                          end={end}
                          className={() =>
                            cn(
                              'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition',
                              isNavItemActive(to, location.pathname, end)
                                ? 'bg-brand-50 text-brand-700'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                            )
                          }
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span>{display}</span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="flex h-16 items-center justify-between border-b border-slate-200 bg-white px-6">
          <div className="flex items-center gap-3">
            <SchoolSwitcher />
            <div className="relative w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                placeholder="Search students, staff…"
                className="w-full rounded-md border border-slate-200 bg-slate-50 py-1.5 pl-9 pr-3 text-sm outline-none focus:border-brand-400 focus:bg-white focus:ring-1 focus:ring-brand-400"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {currentAcademicYear && (
              <>
                <div
                  className="flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700"
                  title="Current session year"
                >
                  <CalendarRange className="h-3.5 w-3.5" />
                  {currentAcademicYear.name}
                </div>
                <div className="h-6 w-px bg-slate-200" />
              </>
            )}
            <NotificationsBell />
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-3">
              <div className="text-right leading-tight">
                <div className="text-sm font-medium text-slate-800">
                  {user?.name ?? 'User'}
                </div>
                <div className="text-xs capitalize text-slate-500">
                  {user?.role}
                </div>
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-600 text-sm font-medium text-white">
                {(user?.name ?? 'U').charAt(0).toUpperCase()}
              </div>
              <button
                onClick={handleLogout}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-red-600"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </header>

        <TrialBanner />
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
